import { z } from "zod";
import type { Parameter, Tool } from "@/app/tools/types";

export const extractParameterValues = (
  tool: Tool,
  input?: Record<string, unknown>
) =>
  tool.input?.parameters?.reduce(
    (acc, param) => {
      acc[param.name] = input?.[param.name] ?? undefined;
      return acc;
    },
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    {} as Record<string, unknown>
  );

type ValueSchema = Parameter["value_schema"];

const jsonValidator = z.string().transform((value, ctx) => {
  if (value === "") {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch {
    ctx.addIssue({
      code: "custom",
      message: "Invalid JSON",
    });
    return z.NEVER;
  }
});

const arrayValidator = z.union([
  z.array(z.string()),
  z.string().transform((str) =>
    str
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s !== "")
  ),
]);

const validatorsByType: Record<string, (schema: ValueSchema) => z.ZodTypeAny> =
  {
    string: (schema) =>
      schema.enum?.length
        ? // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
          z.enum(schema.enum as [string, ...string[]])
        : z.string(),
    integer: () => z.coerce.number().int(),
    number: () => z.coerce.number(),
    boolean: () =>
      z.union([z.enum(["true", "false"]), z.literal("")]).transform((val) => {
        if (val === "") {
          return;
        }
        return val === "true";
      }),
    json: () => jsonValidator,
    array: () => arrayValidator,
  };

function createValidator(schema: ValueSchema): z.ZodTypeAny {
  const factory = validatorsByType[schema.val_type];
  return factory ? factory(schema) : z.string();
}

function applyRequiredConstraint(
  validator: z.ZodTypeAny,
  required: boolean
): z.ZodTypeAny {
  if (required) {
    return validator.refine(
      (val) => {
        if (val === undefined || val === null || val === "") {
          return false;
        }
        // Check for empty arrays
        if (Array.isArray(val) && val.length === 0) {
          return false;
        }
        return true;
      },
      { message: "This field is required" }
    );
  }
  return validator.optional().nullable();
}

export function generateParameterSchema(parameters: Parameter[]) {
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const param of parameters) {
    const validator = createValidator(param.value_schema);
    shape[param.name] = applyRequiredConstraint(
      validator,
      param.required ?? false
    );
  }

  return z.object(shape);
}
